// ============================================================================
// Advisory form validation — small reusable helper (scoped to this form only).
// ----------------------------------------------------------------------------
// Produces a field-keyed error map so the modal can render inline errors at the
// field they belong to, matching the mockup. Kept deliberately independent of
// BaseFormModal so other modals are unaffected. Mirrors the backend validators
// in ADVISORY_RECS_IMPLEMENTATION_SPEC §2.2 (recurrence rules, E2 end-in-future)
// so the user sees the same failures inline before Save rather than as popups.
// ============================================================================

import type { EditingAdvisory } from './AdvisoryFormModal';

export const RECURRENCE_DAY_CODES = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;
export type RecurrenceDayCode = (typeof RECURRENCE_DAY_CODES)[number];

export const RECURRENCE_DAY_LABELS: Record<RecurrenceDayCode, string> = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun',
};

/** Keys match the field names used in the form so errors render at the field. */
export type AdvisoryFieldErrors = {
  advisoryTypeId?: string;
  recipientType?: string;
  npsdClient?: string;
  subject?: string;
  details?: string;
  endDateTime?: string;
  recurrenceDays?: string;
  recurrenceStartTime?: string;
  recurrenceEndTime?: string;
};

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Parse an "HH:mm" string into minutes-since-midnight, or null if malformed. */
export function hhmmToMinutes(value: string | null | undefined): number | null {
  if (!value || !HHMM_RE.test(value)) return null;
  const [h, m] = value.split(':').map((n) => parseInt(n, 10));
  return h * 60 + m;
}

/** Format an "HH:mm" 24h string as a human "7:00 AM" wall-clock label. */
export function formatHhmmForDisplay(value: string | null | undefined): string {
  const mins = hhmmToMinutes(value);
  if (mins == null) return '';
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h24 < 12 ? 'AM' : 'PM';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${m < 10 ? '0' + m : m} ${ampm}`;
}

/** The browser's IANA time zone, sent to the backend as recurrenceZoneId. */
export function browserZoneId(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

interface ValidateOptions {
  /** E2 end-in-future only applies on create, not on edit (spec §2.2). */
  isNew: boolean;
}

/**
 * Validate the advisory being edited. Returns a field-keyed error map; empty
 * means valid. Recurrence rules only apply when the temporary window is on and
 * Recurring is selected; one-time rules apply for the one-time variant.
 */
export function validateAdvisory(
  adv: EditingAdvisory,
  { isNew }: ValidateOptions
): AdvisoryFieldErrors {
  const errors: AdvisoryFieldErrors = {};

  if (!adv.advisoryTypeId) {
    errors.advisoryTypeId = 'Type is required';
  }
  if (!adv.recipientType) {
    errors.recipientType = 'Recipients are required';
  }
  if (adv.recipientType === 'c' && !adv.npsdClient) {
    errors.npsdClient = 'Client is required';
  }
  if (!adv.subject || !adv.subject.trim()) {
    errors.subject = 'Subject is required';
  }
  if (!adv.details || !adv.details.trim()) {
    errors.details = 'Details are required';
  }

  if (adv.isTemporary && adv.recurring) {
    // Recurring: weekday set + both times, start != end. End before start is
    // valid and means an overnight window ending the following day.
    const days = adv.recurrenceDays || [];
    if (days.length === 0) {
      errors.recurrenceDays = 'Select at least one day of the week';
    }
    const startMin = hhmmToMinutes(adv.recurrenceStartTime);
    const endMin = hhmmToMinutes(adv.recurrenceEndTime);
    if (startMin == null) {
      errors.recurrenceStartTime = 'A valid start time is required';
    }
    if (endMin == null) {
      errors.recurrenceEndTime = 'A valid end time is required';
    }
    if (startMin != null && endMin != null && endMin === startMin) {
      errors.recurrenceEndTime = 'End time cannot be the same as the start time';
    }
    // Outer range end-in-future (create only).
    if (isNew && adv.endDateTime && adv.endDateTime.getTime() <= Date.now() - 60000) {
      errors.endDateTime = 'End date/time must be in the future';
    }
  } else if (adv.isTemporary) {
    // One-time: end required, after start, and (create only) in the future.
    if (!adv.endDateTime) {
      errors.endDateTime = 'An auto-remove date is required';
    } else {
      if (adv.startDateTime && adv.endDateTime <= adv.startDateTime) {
        errors.endDateTime = 'Auto-remove date must be after the start date';
      } else if (isNew && adv.endDateTime.getTime() <= Date.now() - 60000) {
        errors.endDateTime = 'End date/time must be in the future';
      }
    }
  }

  return errors;
}

export function hasErrors(errors: AdvisoryFieldErrors): boolean {
  return Object.keys(errors).length > 0;
}
